import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Tag, ArrowLeft, LogOut, Plus, Trash2, UploadCloud, Loader2,
  FileDown, Sparkles, Images, FolderOpen, CheckCircle2, Store, ListChecks,
} from "lucide-react";

export default function PriceTags() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState("list");
  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const [activeSet, setActiveSet] = useState(null);
  const [items, setItems] = useState([]);
  const [autoName, setAutoName] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [genPdf, setGenPdf] = useState("");
  const [filter, setFilter] = useState("alle");
  const [selected, setSelected] = useState({});
  const fileRef = useRef(null);

  const loadSets = () => {
    setLoading(true);
    api.get("/price-tags/sets").then((r) => setSets(r.data.sets)).finally(() => setLoading(false));
  };
  useEffect(() => { loadSets(); }, []);

  const createSet = async () => {
    setCreating(true);
    try {
      const r = await api.post("/price-tags/sets", { name: newName.trim() || "Nieuwe set" });
      setNewName("");
      await openSet(r.data.id);
      loadSets();
    } catch (e) { toast.error("Aanmaken mislukt"); }
    finally { setCreating(false); }
  };

  const openSet = async (id) => {
    const r = await api.get(`/price-tags/sets/${id}`);
    setActiveSet(r.data.set);
    setItems(r.data.items);
    setSelected({});
    setView("set");
  };

  const deleteSet = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Deze set en al zijn kaartjes verwijderen?")) return;
    await api.delete(`/price-tags/sets/${id}`);
    loadSets();
  };

  const onUpload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("auto_name", autoName ? "true" : "false");
      const r = await api.post(`/price-tags/sets/${activeSet.id}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      toast.success(`${r.data.added} kaartje(s) toegevoegd${autoName ? " (namen via AI)" : ""}.`);
      await openSet(activeSet.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload mislukt");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const patchItem = async (id, field, value) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, [field]: value } : it)));
    try { await api.patch(`/price-tags/items/${id}`, { [field]: value }); }
    catch (e) { toast.error("Opslaan mislukt"); }
  };

  const toggleConfirm = async (id, value) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, confirmed: value } : it)));
    try { await api.patch(`/price-tags/items/${id}`, { confirmed: value }); }
    catch (e) { toast.error("Opslaan mislukt"); }
  };

  const selectedIds = () => Object.keys(selected).filter((k) => selected[k]);

  const bulkConfirm = async (value) => {
    const ids = selectedIds();
    if (!ids.length) { toast.info("Selecteer eerst kaartjes."); return; }
    try {
      await api.post(`/price-tags/items/confirm`, { ids, confirmed: value });
      setItems((prev) => prev.map((it) => (ids.includes(it.id) ? { ...it, confirmed: value } : it)));
      setSelected({});
      toast.success(`${ids.length} kaartje(s) ${value ? "bevestigd" : "op te bevestigen gezet"}.`);
    } catch (e) { toast.error("Actie mislukt"); }
  };

  const deleteItem = async (id) => {
    await api.delete(`/price-tags/items/${id}`);
    setItems((prev) => prev.filter((it) => it.id !== id));
  };

  const downloadPdf = async (store) => {
    setGenPdf(store);
    try {
      const r = await api.get(`/price-tags/sets/${activeSet.id}/pdf?store=${store}&confirmed_only=true`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      const lbl = store === "BR" ? "Brustem" : "Sint-Truiden";
      a.href = url;
      a.download = `${(activeSet.name || "prijskaartjes").replace(/\s+/g, "_")}_${lbl}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("PDF genereren mislukt"); }
    finally { setGenPdf(""); }
  };

  const fmtEur = (v) => {
    const s = (v || "").trim();
    if (!s) return "";
    return s.includes("€") ? s : `€${s}`;
  };

  const patchPrice = (id, field, el) => {
    const formatted = fmtEur(el.value);
    el.value = formatted;
    patchItem(id, field, formatted);
  };

  const confirmedCount = items.filter((it) => it.confirmed).length;
  const visibleItems = items.filter((it) =>
    filter === "alle" ? true : filter === "bevestigd" ? it.confirmed : !it.confirmed);
  const groupedByCategory = (() => {
    const groups = {};
    visibleItems.forEach((it) => {
      const c = (it.category || "").trim() || "Zonder soort";
      (groups[c] = groups[c] || []).push(it);
    });
    return Object.entries(groups)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([c, arr]) => [c, arr.sort((x, y) => (x.name || "").localeCompare(y.name || ""))]);
  })();
  const selectAllVisible = () => {
    const next = {};
    visibleItems.forEach((it) => { next[it.id] = true; });
    setSelected(next);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="price-tags-page">
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-md bg-rose-600 flex items-center justify-center text-white">
              <Tag className="h-5 w-5" />
            </div>
            <div className="leading-tight">
              <div className="font-heading font-semibold text-sm">Price Tags — Prijskaartjes</div>
              <div className="text-xs text-muted-foreground">{user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate("/hub")} data-testid="pt-to-hub">
              <ArrowLeft className="h-4 w-4 mr-1" /> Modules
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/"); }}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {view === "list" && (
          <div className="space-y-6">
            <div>
              <h1 className="font-heading text-2xl font-semibold">Prijskaartjes</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Upload foto's (los, in bulk, of één PDF vol foto's) en genereer identieke prijskaartjes
                (3×4 cm, 6×7 per A4). De productnaam wordt automatisch afgeleid; jij zet de prijs.
              </p>
            </div>

            <div className="flex flex-wrap items-end gap-3 rounded-lg border p-4 bg-card">
              <div className="flex-1 min-w-[220px]">
                <label className="text-xs text-muted-foreground">Naam van de set</label>
                <Input value={newName} onChange={(e) => setNewName(e.target.value)}
                  placeholder="bv. Dranken augustus" data-testid="pt-new-set-name"
                  onKeyDown={(e) => e.key === "Enter" && createSet()} />
              </div>
              <Button onClick={createSet} disabled={creating} className="bg-rose-600 hover:bg-rose-700" data-testid="pt-create-set-btn">
                {creating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                Nieuwe set
              </Button>
            </div>

            {loading ? (
              <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Laden…</div>
            ) : sets.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <Images className="h-10 w-10 mx-auto mb-3 opacity-40" />
                Nog geen sets. Maak er één aan om foto's te uploaden.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {sets.map((s) => (
                  <div key={s.id} onClick={() => openSet(s.id)} data-testid={`pt-set-${s.id}`}
                    className="group cursor-pointer rounded-lg border p-4 bg-card hover:border-rose-400 hover:shadow-sm transition">
                    <div className="flex items-start justify-between">
                      <div className="h-10 w-10 rounded-md bg-rose-50 text-rose-600 flex items-center justify-center">
                        <FolderOpen className="h-5 w-5" />
                      </div>
                      <button onClick={(e) => deleteSet(s.id, e)} data-testid={`pt-delete-set-${s.id}`}
                        className="text-muted-foreground hover:text-rose-600 opacity-0 group-hover:opacity-100 transition">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mt-3 font-medium">{s.name}</div>
                    <div className="text-xs text-muted-foreground">{s.item_count} kaartje(s)</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {view === "set" && activeSet && (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => { setView("list"); loadSets(); }} data-testid="pt-back">
                  <ArrowLeft className="h-4 w-4 mr-1" /> Sets
                </Button>
                <h1 className="font-heading text-xl font-semibold">{activeSet.name}</h1>
                <span className="text-sm text-muted-foreground">· {items.length} kaartje(s)</span>
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={() => downloadPdf("ST")} disabled={genPdf !== "" || confirmedCount === 0} data-testid="pt-pdf-st"
                  variant="outline" className="border-rose-300 text-rose-700">
                  {genPdf === "ST" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileDown className="h-4 w-4 mr-1" />}
                  PDF Sint-Truiden
                </Button>
                <Button onClick={() => downloadPdf("BR")} disabled={genPdf !== "" || confirmedCount === 0} data-testid="pt-pdf-br"
                  className="bg-rose-600 hover:bg-rose-700">
                  {genPdf === "BR" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileDown className="h-4 w-4 mr-1" />}
                  PDF Brustem
                </Button>
              </div>
            </div>

            <div className="rounded-lg border p-4 bg-card space-y-3">
              <div className="flex flex-wrap items-center gap-4">
                <Button onClick={() => fileRef.current?.click()} disabled={uploading} variant="outline" data-testid="pt-upload-btn">
                  {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UploadCloud className="h-4 w-4 mr-2" />}
                  Foto's of PDF uploaden
                </Button>
                <input ref={fileRef} type="file" multiple accept="image/*,application/pdf" className="hidden"
                  data-testid="pt-upload-input" onChange={(e) => onUpload(e.target.files)} />
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                  <input type="checkbox" checked={autoName} onChange={(e) => setAutoName(e.target.checked)}
                    data-testid="pt-auto-name-toggle" className="h-4 w-4 accent-rose-600" />
                  <Sparkles className="h-3.5 w-3.5 text-rose-600" /> Naam + soort automatisch lezen (AI)
                </label>
              </div>
              <p className="text-xs text-muted-foreground">
                Los, in bulk of één PDF met foto's — elke foto wordt een kaartje. {uploading && "Bezig met uploaden en lezen…"}
              </p>
            </div>

            {/* Filter + bulk-acties */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-1 rounded-lg border p-1 bg-card">
                {[["alle", "Alle"], ["te", "Te bevestigen"], ["bevestigd", "Bevestigd"]].map(([k, lbl]) => (
                  <button key={k} onClick={() => setFilter(k)} data-testid={`pt-filter-${k}`}
                    className={`px-3 py-1.5 text-sm rounded-md transition ${filter === k ? "bg-rose-600 text-white" : "hover:bg-slate-100"}`}>
                    {lbl}{k === "bevestigd" ? ` (${confirmedCount})` : ""}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={selectAllVisible} data-testid="pt-select-all">
                  <ListChecks className="h-4 w-4 mr-1" /> Alles selecteren
                </Button>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => bulkConfirm(true)} data-testid="pt-bulk-confirm">
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Bevestig geselecteerde
                </Button>
                <Button size="sm" variant="outline" onClick={() => bulkConfirm(false)} data-testid="pt-bulk-unconfirm">
                  Ontbevestigen
                </Button>
              </div>
            </div>

            {visibleItems.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <Images className="h-10 w-10 mx-auto mb-3 opacity-40" />
                {items.length === 0 ? "Nog geen kaartjes. Upload foto's of een PDF." : "Geen kaartjes in deze weergave."}
              </div>
            ) : (
              groupedByCategory.map(([cat, catItems]) => (
                <div key={cat} className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-600">
                    <Store className="h-4 w-4 text-rose-600" /> {cat}
                    <span className="text-xs font-normal text-muted-foreground">({catItems.length})</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                    {catItems.map((it) => (
                      <div key={it.id} data-testid={`pt-item-${it.id}`}
                        className={`rounded-lg border bg-card overflow-hidden relative ${it.confirmed ? "ring-1 ring-emerald-400" : ""}`}>
                        <div className="absolute top-1.5 left-1.5 z-10">
                          <input type="checkbox" checked={!!selected[it.id]} data-testid={`pt-select-${it.id}`}
                            onChange={(e) => setSelected((p) => ({ ...p, [it.id]: e.target.checked }))}
                            className="h-4 w-4 accent-rose-600" />
                        </div>
                        {it.confirmed && (
                          <div className="absolute top-1.5 right-1.5 z-10 rounded-full bg-emerald-500 text-white p-0.5" data-testid={`pt-confirmed-${it.id}`}>
                            <CheckCircle2 className="h-4 w-4" />
                          </div>
                        )}
                        <div className="aspect-[3/4] bg-slate-50 flex items-center justify-center overflow-hidden">
                          <img src={`${API}${it.image_url}`} alt={it.name} className="max-h-full max-w-full object-contain" />
                        </div>
                        <div className="p-2 space-y-1.5">
                          <Input defaultValue={it.name} placeholder="Productnaam" className="h-8 text-sm"
                            data-testid={`pt-name-${it.id}`}
                            onBlur={(e) => e.target.value !== it.name && patchItem(it.id, "name", e.target.value)} />
                          <Input defaultValue={it.category} placeholder="Soort" className="h-7 text-xs"
                            data-testid={`pt-category-${it.id}`}
                            onBlur={(e) => e.target.value !== it.category && patchItem(it.id, "category", e.target.value)} />
                          <div className="grid grid-cols-2 gap-1">
                            <div className="flex items-center gap-1">
                              <span className="text-[10px] text-muted-foreground w-5">ST</span>
                              <Input defaultValue={it.price_st} placeholder="4,50" className="h-7 text-xs font-semibold px-1"
                                data-testid={`pt-price-st-${it.id}`}
                                onBlur={(e) => e.target.value.trim() !== (it.price_st || "").trim() && patchPrice(it.id, "price_st", e.target)} />
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-[10px] text-muted-foreground w-5">BR</span>
                              <Input defaultValue={it.price_br} placeholder="4,50" className="h-7 text-xs font-semibold px-1"
                                data-testid={`pt-price-br-${it.id}`}
                                onBlur={(e) => e.target.value.trim() !== (it.price_br || "").trim() && patchPrice(it.id, "price_br", e.target)} />
                            </div>
                          </div>
                          <div className="flex items-center gap-1 pt-0.5">
                            <button onClick={() => toggleConfirm(it.id, !it.confirmed)} data-testid={`pt-toggle-confirm-${it.id}`}
                              className={`flex-1 h-7 rounded text-xs font-medium transition ${it.confirmed ? "bg-emerald-50 text-emerald-700 border border-emerald-300" : "bg-slate-100 hover:bg-slate-200"}`}>
                              {it.confirmed ? "Bevestigd ✓" : "Bevestigen"}
                            </button>
                            <button onClick={() => deleteItem(it.id)} data-testid={`pt-delete-item-${it.id}`}
                              className="h-7 w-7 shrink-0 rounded border text-muted-foreground hover:text-rose-600 hover:border-rose-300 flex items-center justify-center">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
}
